import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { IconSvg } from 'components/common'
import { Fragment, PropsWithChildren, useMemo, useState } from 'react'
import { useStore } from 'hooks/useStore'
import { Link, useNavigate } from 'react-router-dom'
import { devMode, CLUB_PERMISSIONS } from 'utils/constants'
import { ManageClubType } from 'graphql/type'
import { ChooseActionClubModal } from 'containers/common'
import CreateEventRestrictedModal from 'containers/common/create-event-restricted-modal'
import { CreateSeriesModal } from 'containers/rep-clubs/events/create-series-modal'
import { hasClubPermission } from 'utils/permissions'

interface Props {
	position?: string
}

export default function CreateActionDropdown({ position }: PropsWithChildren<Props>) {
	const { myClubs, user } = useStore()
	const navigate = useNavigate()
	const showRepClubFeatures = devMode('Create menu: Club, Offer & Achievement items')
	const [openModal, setOpenModal] = useState<null | 'event' | 'offer' | 'achievement'>(null)
	const [showRestrictedModal, setShowRestrictedModal] = useState(false)
	const [openSeriesModal, setOpenSeriesModal] = useState(false)
	const [showSeriesRestrictedModal, setShowSeriesRestrictedModal] = useState(false)
	const userId = user?.userId

	const ownerClubs = useMemo(() => {
		if (!myClubs || myClubs.length === 0 || !userId) {
			return []
		}

		return myClubs.filter((club: ManageClubType) => club.owner?.userId === userId)
	}, [myClubs, userId])

	const canCreateEventClubs = useMemo(() => {
		if (!myClubs) {
			return []
		}
		return myClubs.filter((club: ManageClubType) =>
			hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_EVENTS)
		)
	}, [myClubs])

	const canCreateOfferClubs = useMemo(() => {
		if (!myClubs) {
			return []
		}
		return myClubs.filter((club: ManageClubType) =>
			hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_OFFERS)
		)
	}, [myClubs])

	const canCreateAchievementClubs = useMemo(() => {
		if (!myClubs) {
			return []
		}
		return myClubs.filter((club: ManageClubType) =>
			hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_ACHIEVEMENTS)
		)
	}, [myClubs])

	const isClubVisible = ownerClubs.length === 0
	const isEventEnabled = canCreateEventClubs.length > 0 || !showRepClubFeatures
	const isOfferEnabled = canCreateOfferClubs.length > 0
	const isAchievementEnabled = canCreateAchievementClubs.length > 0

	const getClubSlug = (club: ManageClubType) => club.slug ?? String(club.clubId)

	const handleClubSelect = (club: ManageClubType, action: string) => {
		const clubSlug = getClubSlug(club)

		switch (action) {
			case 'event':
				navigate(`/manage/rep-clubs/${clubSlug}/events/create?tab=info`)
				break
			case 'offer':
				navigate(`/manage/rep-clubs/${clubSlug}/offers/create/select-offer-type`)
				break
			case 'achievement':
				navigate(`/manage/rep-clubs/${clubSlug}/achievements/create`)
				break
		}
		setOpenModal(null)
	}

	return (
		<div>
			<Menu as="div" className="relative text-left">
				<div className="flex items-center">
					<MenuButton className="relative">
						<IconSvg icon="add-2" width={20} height={20} />
					</MenuButton>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<MenuItems
						className={clsx(
							'fixed inset-0 h-[100dvh] sm:absolute sm:inset-auto sm:h-auto sm:right-0 rounded-none sm:rounded-lg shadow-box bg-primary-surface/[0.4] backdrop-blur-lg focus:outline-none sm:mt-1 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-4 sm:w-[220px] w-full z-50 sm:z-10 ring-1 ring-black ring-opacity-5 overflow-hidden flex flex-col border border-translucent-plus-border',
							{
								'origin-top-right': position !== 'top',
								'origin-bottom-right bottom-full': position === 'top',
							}
						)}
					>
						<div className="flex justify-between items-center flex-shrink-0 pb-2 border-b border-translucent-plus-border sm:border-none mb-2">
							<p className="text-base font-medium">Create</p>
							<MenuItem>
								{({ close }) => (
									<button
										type="button"
										onMouseDown={e => {
											e.preventDefault()
											e.stopPropagation()
										}}
										onClick={e => {
											e.preventDefault()
											e.stopPropagation()
											setTimeout(() => {
												close()
											}, 0)
										}}
										className="sm:hidden flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/5 transition-colors"
										aria-label="Close create menu"
									>
										<IconSvg icon="close" />
									</button>
								)}
							</MenuItem>
						</div>
						<div className="flex-1 overflow-auto space-y-1">
							{showRepClubFeatures && isClubVisible && (
								<MenuItem>
									{({ active }) => (
										<Link
											to="/manage/rep-clubs/create"
											className={clsx(
												'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300 hover:text-primary-text',
												{
													'text-white': active,
													'text-primary-text': !active,
												}
											)}
										>
											<IconSvg icon="repclub" className="text-white w-5" aria-hidden="true" />
											<span>Club</span>
										</Link>
									)}
								</MenuItem>
							)}

							{isEventEnabled && (
								<MenuItem>
									{({ active }) => (
										<button
											type="button"
											onClick={() => {
												if (!user) return
												if (!user.enableCreateEvent) {
													setOpenModal(null)
													setShowRestrictedModal(true)
												} else {
													setOpenModal('event')
												}
											}}
											className={clsx(
												'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300 hover:text-primary-text w-full text-left',
												{
													'text-white': active,
													'text-primary-text': !active,
												}
											)}
										>
											<IconSvg icon="event" className="text-white w-5" aria-hidden="true" />
											<span>Event</span>
										</button>
									)}
								</MenuItem>
							)}

							{isEventEnabled && (
								<MenuItem>
									{({ active }) => (
										<button
											type="button"
											onClick={() => {
												if (!user) return
												if (!user.enableCreateEvent) {
													setShowSeriesRestrictedModal(true)
												} else {
													setOpenSeriesModal(true)
												}
											}}
											className={clsx(
												'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300 hover:text-primary-text w-full text-left',
												{
													'text-white': active,
													'text-primary-text': !active,
												}
											)}
										>
											<IconSvg icon="calendar" className="text-white w-5" aria-hidden="true" />
											<span>Event series</span>
										</button>
									)}
								</MenuItem>
							)}

							{showRepClubFeatures && (
								<>
									{isOfferEnabled && (
										<MenuItem>
											{({ active }) => (
												<button
													type="button"
													onClick={() => setOpenModal('offer')}
													className={clsx(
														'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300 hover:text-primary-text w-full text-left',
														{
															'text-white': active,
															'text-primary-text': !active,
														}
													)}
												>
													<IconSvg
														icon="rewards"
														className="text-white w-5"
														aria-hidden="true"
													/>
													<span>Offer</span>
												</button>
											)}
										</MenuItem>
									)}
									{showRepClubFeatures && isAchievementEnabled && (
										<MenuItem>
											{({ active }) => (
												<button
													type="button"
													onClick={() => setOpenModal('achievement')}
													className={clsx(
														'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300 hover:text-primary-text w-full text-left',
														{
															'text-white': active,
															'text-primary-text': !active,
														}
													)}
												>
													<IconSvg icon="cup" className="text-white w-5" aria-hidden="true" />
													<span>Achievement</span>
												</button>
											)}
										</MenuItem>
									)}
								</>
							)}
						</div>
					</MenuItems>
				</Transition>
			</Menu>

			{openModal && (
				<ChooseActionClubModal
					open={openModal !== null}
					setOpen={open => {
						if (!open) setOpenModal(null)
					}}
					action={openModal}
					onSelect={club => handleClubSelect(club, openModal)}
				/>
			)}

			<CreateEventRestrictedModal
				open={showRestrictedModal}
				setOpen={setShowRestrictedModal}
			/>

			<CreateSeriesModal
				open={openSeriesModal}
				setOpen={setOpenSeriesModal}
				basePath="/manage/rep-clubs/events"
			/>

			<CreateEventRestrictedModal
				open={showSeriesRestrictedModal}
				setOpen={setShowSeriesRestrictedModal}
				title="Event Series Creation Restricted"
				description="You're currently restricted from creating event series. If you would like to get approved, contact us and we will reach out soon."
			/>
		</div>
	)
}
